// v0.90 — the live FxSource. One Face ID against M's own buffer.
//
// Shape is TellerEarnSource's: balances read straight from chain, the move
// submitted as a UserOp through Circle's bundler. The difference is the price —
// a swap needs one, and M is the counterparty, so the Worker signs an EIP-712
// voucher naming the exact pair of amounts and `MFx` checks that signature.
// M prices; the customer still signs and submits. Swapping this in for
// `makePlaceholderFxSource` is a one-line change in dropCatchView.
//
// **The rate cache is the point.** `fxSheet.refreshQuote()` asks for a unit
// rate AND an amount rate on every (debounced) keystroke. Against a placeholder
// that was free; against the Worker it would be two requests per keypress. But
// with a flat spread the rate does not depend on the amount — so `quote()`
// serves from a short-lived cache and does the arithmetic locally, and the
// network sees roughly one poll every few seconds however fast someone types.
// That is what keeps the keypad instant, and it needed no change to `FxSource`.

import { encodeFunctionData } from "viem";
import { recordMeasure } from "../diag";
import { PREPARED_TTL_MS, sponsorOnTouch, type WalletAdapter } from "./WalletAdapter";
import { currency, liveCurrencies, type FxQuote, type FxSource, type FxCapacity, type FxTopUp, type FxPrepared } from "../fx";

/** How long a polled rate is good for. Comfortably shorter than the Worker's
 *  own 5 s upstream cache, so the two don't beat against each other. */
const RATE_TTL_MS = 4_000;

// (DRIFT_TOLERANCE, the symmetric 1 % of v0.90 – 1.4.20, is gone: 1.4.21 stops
// a swap only when the rate moved against the person — see swap() step 2.)

const ERC20_ABI = [
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
] as const;

const MFX_ABI = [
  {
    type: "function",
    name: "swap",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "v",
        type: "tuple",
        components: [
          { name: "user", type: "address" },
          { name: "tokenIn", type: "address" },
          { name: "tokenOut", type: "address" },
          { name: "amountIn", type: "uint256" },
          { name: "amountOut", type: "uint256" },
          { name: "deadline", type: "uint256" },
          { name: "nonce", type: "uint256" },
        ],
      },
      { name: "sig", type: "bytes" },
    ],
    outputs: [],
  },
] as const;

/** A refusal the user should read as a sentence, not as a failed transaction. */
/** The Worker said no (or nothing usable). `code` is its error string and
 *  `status` the HTTP status — carried so Lab can show them (v0.90.13: the first
 *  phone swap died on a Worker 500 and the phone said only "Couldn't complete
 *  that swap."). The `cause` chain is what `recordError` flattens. */
export class FxRefused extends Error {
  constructor(message: string, readonly code?: string, readonly status?: number) {
    super(message, {
      cause: new Error(`fx/voucher ${status ?? "?"} ${code ?? "(no error field in body)"}`),
    });
    this.name = "FxRefused";
  }
}

/** The price moved between the card and the slide. Carries the new figure. */
export class FxRateMoved extends Error {
  constructor(readonly amountOut: number) {
    super("rate_moved");
  }
}

/** What the Worker's refusals mean to a person. Anything unmapped falls through
 *  to the sheet's generic message — better silent than wrong. */
const REFUSALS: Record<string, string> = {
  limit_reached: "You've used all your swaps for the beta.",
  amount_above_limit: "That's above the swap limit for now.",
  buffer_short: "Not enough in that pocket to swap into right now.",
  fx_paused: "Swapping is paused for a moment.",
  fx_not_enabled: "Swapping isn't switched on yet.",
  pair_not_supported: "M can't swap that pair yet.",
  rate_unavailable: "Couldn't get a rate just now.",
  chain_unreachable: "Couldn't reach the network just now.",
};

interface CachedRate {
  rate: number;
  at: number;
  expiresAt: number;
  source: string;
}

export interface MFxConfig {
  /** Base URL for the Worker's /fx/* endpoints. */
  apiBase: string;
  /** The wallet this source acts for. Passed in rather than read back from the
   *  adapter, so a mis-wiring is a compile error and never a swap signed for
   *  the wrong account. */
  address: string;
}

export class MFxSource implements FxSource {
  readonly live = true;
  private readonly rates = new Map<string, CachedRate>();

  constructor(
    private readonly adapter: WalletAdapter,
    private readonly cfg: MFxConfig,
  ) {}

  /** Every pocket M can actually hold, read from chain. Not on the Swap
   *  sheet's path (it takes balances from the home view, which already has
   *  them) — but the seam promises it, so it works. */
  async getPockets(address: string): Promise<Record<string, number>> {
    // 1.5.31 — a read that failed is not an empty pocket: the pay page and the checkout steer a person to Add money on
    // what these say, so a failed read throws and the plan says "unknown" (the chain decides, as before) instead of
    // "Not enough in your wallet" for money that is there.
    const out: Record<string, number> = {};
    // 1.5.33 — each read that fails is tried once more: on a phone one of the eleven fails now and then, and a single
    // failure made the whole check "unknown", so a short wallet reached the chain's refusal (Alexander's recording).
    let failed = 0;
    const read = async (code: string) => Number(await this.adapter.getBalance(address, code)) || 0;
    await Promise.all(
      liveCurrencies().map(async (code) => {
        try {
          out[code] = await read(code);
        } catch {
          try { out[code] = await read(code); }
          catch { out[code] = 0; failed += 1; }
        }
      }),
    );
    if (failed) throw new Error("pocket_read_failed");
    return out;
  }

  private async rateFor(from: string, to: string): Promise<CachedRate> {
    const key = `${from}/${to}`;
    const hit = this.rates.get(key);
    if (hit && Date.now() - hit.at < RATE_TTL_MS) return hit;

    const res = await fetch(
      `${this.cfg.apiBase}/fx/rate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
    if (!res.ok) {
      // A stale rate beats no rate while the sheet is open; past that, say so.
      if (hit) return hit;
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      throw new FxRefused(REFUSALS[j.error ?? ""] ?? "Couldn't get a rate just now.", j.error, res.status);
    }
    const j = (await res.json()) as { rate: number; expiresAt: number; source: string };
    const fresh: CachedRate = {
      rate: j.rate,
      at: Date.now(),
      expiresAt: j.expiresAt,
      source: j.source,
    };
    this.rates.set(key, fresh);
    return fresh;
  }

  /** Local arithmetic against the cached rate. The rate already carries M's
   *  spread, so `amountIn × rate` is exactly what the person receives — there
   *  is no second deduction between here and the card. */
  async quote(from: string, to: string, amount: number): Promise<FxQuote> {
    const r = await this.rateFor(from, to);
    const dp = currency(to).dp;
    const amountOut = Number((amount * r.rate).toFixed(dp));
    return {
      from,
      to,
      rate: r.rate,
      amountIn: amount,
      amountOut,
      fee: null, // the spread is in the rate; no separate fee line
      expiresAt: r.expiresAt,
    };
  }

  // ---- v0.90.19 — the ladder of sources -----------------------------------
  // What the Worker knows about limits and the buffer, cached briefly: the
  // keypad's ceiling reads it on every keystroke.
  private cfgCache: { at: number; maxAmount: number; buffers: Record<string, number> | null } | null = null;
  private usageCache: { at: number; remaining: number | null } | null = null;

  private async fxConfig() {
    if (this.cfgCache && Date.now() - this.cfgCache.at < 30_000) return this.cfgCache;
    const j = (await (await fetch(`${this.cfg.apiBase}/fx/config`)).json()) as {
      maxAmount?: number; buffers?: Record<string, string> | null;
    };
    const buffers = j.buffers
      ? Object.fromEntries(Object.entries(j.buffers).map(([k, v]) => [k, Number(v) || 0]))
      : null;
    this.cfgCache = { at: Date.now(), maxAmount: Number(j.maxAmount ?? 0), buffers };
    return this.cfgCache;
  }
  private async fxUsage() {
    if (this.usageCache && Date.now() - this.usageCache.at < 30_000) return this.usageCache;
    const j = (await (await fetch(`${this.cfg.apiBase}/fx/usage/${this.cfg.address}`)).json()) as {
      remaining?: number | null;
    };
    this.usageCache = { at: Date.now(), remaining: j.remaining == null ? null : Number(j.remaining) };
    return this.usageCache;
  }

  /** 1.4.29 — what M's pool holds in a currency (null = unknown: let the
   *  voucher decide). Swap opens a currency only when its pool has money. */
  async poolOf(code: string): Promise<number | null> {
    try {
      const c = await this.fxConfig();
      return c.buffers ? (c.buffers[code] ?? 0) : null;
    } catch { return null; }
  }

  /** What rung 3 could cover for this pair right now. A limit of 0 or less in
   *  the Worker's config means "no limit" (production); the beta's caps are
   *  config rows, so the same code runs both. */
  async capacity(from: string, to: string): Promise<FxCapacity> {
    const [r, c, u] = await Promise.all([this.rateFor(from, to), this.fxConfig(), this.fxUsage()]);
    return {
      rate: r.rate,
      maxIn: c.maxAmount > 0 ? c.maxAmount : null,
      remaining: u.remaining,
      bufferOut: c.buffers ? (c.buffers[to] ?? 0) : null,
    };
  }

  /** A voucher for exactly `amountOut` of `to` (the Worker prices the IN side,
   *  rounded up to the token), returned as calls for the payment's own userOp. */
  async topUp(from: string, to: string, amountOut: number): Promise<FxTopUp> {
    const res = await fetch(`${this.cfg.apiBase}/fx/voucher`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ address: this.cfg.address, from, to, amountOut }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      voucher?: { user: string; tokenIn: string; tokenOut: string; amountIn: string; amountOut: string; deadline: string; nonce: string };
      signature?: string;
      contract?: string;
      amountInDisplay?: string;
      amountOutDisplay?: string;
    };
    if (!res.ok || !body.voucher || !body.signature || !body.contract) {
      throw new FxRefused(REFUSALS[body.error ?? ""] ?? "Couldn't convert for this payment.", body.error, res.status);
    }
    const v = body.voucher;
    const voucher = {
      user: v.user as `0x${string}`,
      tokenIn: v.tokenIn as `0x${string}`,
      tokenOut: v.tokenOut as `0x${string}`,
      amountIn: BigInt(v.amountIn),
      amountOut: BigInt(v.amountOut),
      deadline: BigInt(v.deadline),
      nonce: BigInt(v.nonce),
    };
    this.usageCache = null; // a voucher was issued; the allowance moved
    return {
      from,
      to,
      amountIn: Number(body.amountInDisplay ?? "0"),
      amountOut: Number(body.amountOutDisplay ?? String(amountOut)),
      nonce: v.nonce,
      calls: [
        {
          to: v.tokenIn,
          data: encodeFunctionData({ abi: ERC20_ABI, functionName: "approve", args: [body.contract as `0x${string}`, voucher.amountIn] }),
        },
        {
          to: body.contract,
          data: encodeFunctionData({ abi: MFX_ABI, functionName: "swap", args: [voucher, body.signature as `0x${string}`] }),
        },
      ],
    };
  }

  reportTopUp(t: FxTopUp, txHash: string, ok: boolean): void {
    void fetch(`${this.cfg.apiBase}/fx/record`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nonce: t.nonce, txHash, ok }),
    }).catch(() => {});
  }

  /** 1.4.25 — whether background preparation may run: only when the Worker
   *  counts no per-wallet allowance (every prepared voucher counts while it
   *  lives). Before Worker 1.3.8 the beta's 5 apply, and nothing is prepared. */
  async canPrepare(): Promise<boolean> {
    try { return (await this.fxUsage()).remaining === null; } catch { return false; }
  }

  /** 1.4.25 — everything before Face ID: the Worker prices and signs the
   *  voucher, the userOp is prepared (gas, paymaster, nonce). With `quotedOut`
   *  the 1.4.21 rule applies (better → the shown figure; worse → FxRateMoved);
   *  without it (background) the voucher's own figure is what the screen shows. */
  async prepareSwap(q: FxQuote, opts: { quotedOut?: number; noOp?: boolean } = {}): Promise<FxPrepared> {
    const address = this.cfg.address;
    const res = await fetch(`${this.cfg.apiBase}/fx/voucher`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ address, from: q.from, to: q.to, amountIn: q.amountIn, ...(opts.quotedOut != null ? { quotedOut: opts.quotedOut } : {}) }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      amountOut?: string;
      voucher?: { user: string; tokenIn: string; tokenOut: string; amountIn: string; amountOut: string; deadline: string; nonce: string };
      signature?: string;
      contract?: string;
      amountOutDisplay?: string;
      expiresAt?: number;
    };
    if (res.status === 409 && body.error === "rate_moved" && body.amountOut != null) {
      this.rates.delete(`${q.from}/${q.to}`); // the next quote asks the Worker afresh
      throw new FxRateMoved(Number(body.amountOut));
    }
    if (!res.ok || !body.voucher || !body.signature || !body.contract) {
      throw new FxRefused(REFUSALS[body.error ?? ""] ?? "Couldn't complete that swap.", body.error, res.status);
    }
    const priced = Number(body.amountOutDisplay ?? "0");
    // 1.4.21's rule for a Worker that ignores quotedOut: worse by more than half a cent → stop.
    if (opts.quotedOut != null && opts.quotedOut > 0 && priced < opts.quotedOut - 0.005) {
      this.rates.delete(`${q.from}/${q.to}`);
      throw new FxRateMoved(priced);
    }
    const v = body.voucher;
    const voucher = {
      user: v.user as `0x${string}`, tokenIn: v.tokenIn as `0x${string}`, tokenOut: v.tokenOut as `0x${string}`,
      amountIn: BigInt(v.amountIn), amountOut: BigInt(v.amountOut), deadline: BigInt(v.deadline), nonce: BigInt(v.nonce),
    };
    // approve + swap in ONE UserOp → one Face ID.
    const calls = [
      { to: v.tokenIn, data: encodeFunctionData({ abi: ERC20_ABI, functionName: "approve", args: [body.contract as `0x${string}`, voucher.amountIn] }) },
      { to: body.contract, data: encodeFunctionData({ abi: MFX_ABI, functionName: "swap", args: [voucher, body.signature as `0x${string}`] }) },
    ];
    // The userOp prepared now, so the slide goes straight to Face ID. A failure
    // here is not the swap's: sendSwap falls back to the plain submit path.
    // v2.0 — with Circle's stub only; the sponsor's signature waits for touch().
    const op = opts.noOp ? null : await this.adapter.prepare(calls).catch(() => null);
    const voucherEnds = Number(v.deadline) * 1000;
    const opEnds = op ? op.at + PREPARED_TTL_MS : Infinity;
    return {
      quote: { ...q, amountOut: priced },
      amountOut: priced,
      nonce: v.nonce,
      calls,
      op,
      // Good until the first of the two runs out, with 8 s kept back for the slide and Face ID.
      usableUntil: Math.min(voucherEnds, opEnds) - 8_000,
    };
  }

  /** v2.0 — sponsor on touch: the paymaster's signature for the prepared op, asked for as the finger lands. */
  touch(p: FxPrepared): void {
    if (p.op) sponsorOnTouch(this.adapter, p.op);
  }

  /** 1.4.25 — Face ID (the prepared userOp, or a plain submit), then the receipt. */
  async sendSwap(p: FxPrepared, onStage?: (stage: "confirming") => void): Promise<{ txHash: string; amountOut?: number }> {
    const fresh = p.op && Date.now() - p.op.at < PREPARED_TTL_MS - 5_000;
    // 1.4.27 — what Lab needs to tell why iOS still asks "Use Passkey" (16 Oct): which path,
    // how old the prepared op was, and whether the slide still counted as a finger action here.
    const ua = (navigator as unknown as { userActivation?: { isActive: boolean } }).userActivation;
    recordMeasure("swap/path", `${fresh ? "prepared" : "plain"} · op ${p.op ? Math.round((Date.now() - p.op.at) / 1000) + " s old" : "none"} · activation ${ua ? String(ua.isActive) : "n/a"}`);
    const sent = fresh
      ? await this.adapter.sendPrepared(p.op!).catch((e: unknown) => {
          if ((e as Error)?.message === "prepared_stale") return this.adapter.submit(p.calls);
          throw e;
        })
      : await this.adapter.submit(p.calls);
    onStage?.("confirming");
    const settled = await sent.settled;
    if (!settled.success) throw new Error("userop_reverted");
    // Tell the Worker how it went — best-effort, as /tx/record and the Drop flow.
    void fetch(`${this.cfg.apiBase}/fx/record`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nonce: p.nonce, txHash: settled.txHash, ok: true }),
    }).catch(() => {});
    this.usageCache = null;
    // What they actually got, from the voucher M signed — not from the card.
    return { txHash: settled.txHash, amountOut: p.amountOut };
  }

  /** The slide with nothing prepared: both halves at once (the old sheet's path too). */
  async swap(q: FxQuote, onStage?: (stage: "confirming") => void): Promise<{ txHash: string; amountOut?: number }> {
    const p = await this.prepareSwap(q, { quotedOut: q.amountOut, noOp: true });
    return this.sendSwap(p, onStage);
  }
}
